import * as React from 'react';
import { startTransition, Suspense } from 'react';
import { styled, useTheme as useMuiTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import BtnDarkModeToggle from "./Buttons/BtnDarkModeToggle";
import ButtonList from "./Buttons/ButtonList";
import {useContext} from "react";
import {AuthContext} from "../../auth/providers/AuthProvider";
import {GetIcon} from "../../../utils/get_icon/GetIcon";
import {ThemeContext} from "styled-components";
import {useQuery, useMutation, useQueryClient} from "react-query";
import {listUsersTemplates, updateBillFavorite} from "../../../services/template/Templates";
import {CsrfContext} from "../../../providers/CsrfProvider";
import {useNavigate, useLocation} from "react-router-dom";
import {useGroup} from "../../../providers/GroupProvider";
import IconCollapseMenu from "../../../assets/Images/Icons/IconCollapseMenu";
import IconExpandMenu from "../../../assets/Images/Icons/IconExpandMenu";
import IconAddScheduled from "../../../assets/Images/Icons/IconAddScheduled";
import { ShootingStar, ChartBar } from "@phosphor-icons/react";
import ButtonDivider from "./Buttons/ButtonDivider";
import BillMiniDashboard from "./BillMiniDashboard";

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar - fixed at 48px to match header height
    minHeight: '48px',
    height: '48px',
    backgroundColor: 'transparent',
    borderRight: 'none',
    '&::after': {
        display: 'none',
    },
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
            border: 'none !important',
            borderRight: 'none !important',
            overflow: 'visible !important',
        },
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': {
                ...openedMixin(theme),
                border: 'none !important',
                borderRight: 'none !important',
                overflow: 'visible !important',
            },
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': {
                ...closedMixin(theme),
                border: 'none !important',
                borderRight: 'none !important',
                overflow: 'visible !important',
            },
        }),
    }),
);

const ExpandableListItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== 'isHovered' && prop !== 'isOpen' })(
    ({ theme, isHovered, isOpen }) => ({
        position: 'relative',
        zIndex: isHovered ? 1300 : 1,
        overflow: 'visible',
        padding: 0,
    }),
);

const ExpandableButtonWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'isHovered' && prop !== 'isOpen' && prop !== 'bgColor' && prop !== 'isActive' })(
    ({ theme, isHovered, isOpen, bgColor, isActive }) => {
        const closedWidth = `calc(${theme.spacing(7)} + 1px)`;
        const openWidth = `${drawerWidth}px`;
        return {
            position: 'relative',
            width: isHovered ? openWidth : (isOpen ? openWidth : closedWidth),
            minWidth: isHovered ? openWidth : (isOpen ? openWidth : closedWidth),
            maxWidth: isHovered ? openWidth : (isOpen ? openWidth : closedWidth),
            backgroundColor: bgColor || 'transparent',
            transition: 'width 200ms ease, min-width 200ms ease, max-width 200ms ease, transform 200ms ease',
            boxShadow: isHovered && !isOpen ? '4px 0 8px rgba(0, 0, 0, 0.2)' : 'none',
            transform: isActive ? 'translateX(8px)' : 'none',
            overflow: 'visible',
            zIndex: isActive ? 1302 : (isHovered ? 1301 : 'auto'),
            ...(isActive && {
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: '-12px',
                    width: '12px',
                    height: '100%',
                    backgroundColor: bgColor || 'transparent',
                    zIndex: 1303,
                },
            }),
        };
    },
);

const Sidebar = ({children}) => {
    const theme = useContext(ThemeContext);
    let muiTheme;
    try {
        muiTheme = useMuiTheme();
    } catch (e) {
        muiTheme = { spacing: (n) => `${n * 8}px` }; // Fallback
    }
    const [open, setOpen] = React.useState(false);
    const { authClaims } = useContext(AuthContext);
    const { csrfToken, } = useContext(CsrfContext);
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { selectedGroupId, setSelectedGroup, loading: groupsLoading, hydratedBills, billsGroupId } = useGroup();

    const selectedMatchesHydrated = (selectedGroupId == null && (billsGroupId == null || billsGroupId === '')) ||
        (selectedGroupId && billsGroupId && String(selectedGroupId) === String(billsGroupId));
    
    // Get current template ID from URL (UUID format), or default bill view
    const currentTemplateId = React.useMemo(() => {
        const match = location.pathname.match(/\/bills\/templates\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
        return match ? match[1] : null;
    }, [location.pathname]);
    const isDefaultBillActive = location.pathname === '/bills/default';
    const isDashboardActive = location.pathname === '/dashboard';
    const isAddBillActive = location.pathname === '/bills/templates';

    // Only run templates query when we don't already have bills from GET /api/groups?bills=true (avoids GET /api/bills/templates on refresh).
    const { data: fetchedTemplates = [], error, status } = useQuery(
        ["templates", selectedGroupId],
        () => listUsersTemplates(csrfToken, selectedGroupId ?? null),
        {
            enabled: !!authClaims && !!csrfToken && !groupsLoading && !selectedMatchesHydrated,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            retry: (failureCount, error) => {
                if (error?.response?.status === 401 && Date.now() - (window.__lastLoginTime || 0) < 3000) {
                    return false;
                }
                return failureCount < 3;
            }
        }
    );
    const data = selectedMatchesHydrated && Array.isArray(hydratedBills) ? hydratedBills : fetchedTemplates;

    const [archivedExpanded, setArchivedExpanded] = React.useState(false);
    const [hoveredBillId, setHoveredBillId] = React.useState(null);
    const [hoveredItemKey, setHoveredItemKey] = React.useState(null);
    
    // Clear hover states when archived section collapses to prevent conflicts
    React.useEffect(() => {
        if (!archivedExpanded) {
            setHoveredBillId(null);
        }
    }, [archivedExpanded]);
    
    // Split bills into archived and non-archived on the frontend (defensive: always treat as array, skip null entries)
    const { activeBills, archivedBills, hasArchived } = React.useMemo(() => {
        const list = Array.isArray(data) ? data.filter(Boolean) : [];
        if (list.length === 0) {
            return { activeBills: [], archivedBills: [], hasArchived: false };
        }
        
        const active = [];
        const archived = [];
        
        list.forEach(bill => {
            const isArchived = bill.archived_at != null && bill.archived_at !== '';
            if (isArchived) {
                archived.push(bill);
            } else {
                active.push(bill);
            }
        });
        
        // Sort archived bills by archived_at (most recent first)
        archived.sort((a, b) => {
            const dateA = a.archived_at ? new Date(a.archived_at) : new Date(0);
            const dateB = b.archived_at ? new Date(b.archived_at) : new Date(0);
            return dateB - dateA;
        });
        
        return {
            activeBills: active,
            archivedBills: archived,
            hasArchived: archived.length > 0
        };
    }, [data]);

    const favoriteMutation = useMutation(
        ({ id, favorite }) => updateBillFavorite(id, favorite, csrfToken),
        {
            onSuccess: () => {
                // Invalidate and refetch templates after successful favorite toggle
                queryClient.invalidateQueries("templates");
            },
        }
    );

    const handleFavoriteToggle = (e, id, currentFavorite) => {
        e.stopPropagation(); // Prevent navigation when clicking the star
        if (!id) {
            return;
        }
        favoriteMutation.mutate({ id, favorite: !currentFavorite });
    };

    const handleToggleDrawer = () => {
        setOpen(!open);
    };

    const getFavoriteStatus = (bill) => {
        // If user is the owner, always use bill-level favorite
        if (bill.owner_sub && authClaims?.user_sub && 
            bill.owner_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
            return bill.favorite || false;
        }
        
        // If user is a participant (but not owner), use their participant favorite status
        if (bill.participants && bill.participants.length > 0 && authClaims?.user_sub) {
            const userParticipant = bill.participants.find(
                p => p.user_sub && p.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()
            );
            if (userParticipant && userParticipant.favorite !== undefined) {
                return userParticipant.favorite;
            }
        }
        
        return false;
    };

    if (!authClaims) {
        return (<></>)
    }

    return (
            <Drawer variant="permanent" open={open} PaperProps={{
                sx: {
                    backgroundColor: 'transparent',
                    color: "white",
                    zIndex: 1240,
                    border: 'none !important',
                    borderRight: 'none !important',
                    overflow: 'visible',
                    '& .MuiDrawer-paper': {
                        border: 'none !important',
                        borderRight: 'none !important',
                        overflow: 'visible',
                    },
                    '&::before': {
                        display: 'none',
                    },
                    '&::after': {
                        display: 'none',
                    },
            }}}>
                <DrawerHeader/>
                <Box sx={{
                    backgroundColor: theme.colors.primary,
                    minHeight: '100%',
                    boxShadow: theme.shadows?.sidebar || '4px 0 8px rgba(0, 0, 0, 0.15), -4px 0 8px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.15)',
                    borderRight: 'none !important',
                    border: 'none',
                    position: 'relative',
                    overflow: 'visible',
                    width: '100%',
                }}>
                <List sx={{ overflow: 'visible', width: '100%', position: 'relative' }}>
                    <ListItem key={"toggle darkMode"} disablePadding sx={{ display: 'block' }}>
                        <BtnDarkModeToggle/>
                        <ButtonDivider />
                    </ListItem>
                    <ListItem key={"toggle drawer"} disablePadding sx={{ display: 'block' }}>
                        <ButtonList
                            onClick={handleToggleDrawer}
                            justifyContent={open ? 'initial' : 'center'}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                    color: theme.colors.text
                                }}
                            >
                                {open?<IconCollapseMenu size={28}/>:<IconExpandMenu size={28}/>}
                            </ListItemIcon>
                        </ButtonList>
                        <ButtonDivider />
                    </ListItem>
                    <ExpandableListItem
                        key="dashboard"
                        disablePadding
                        sx={{ display: 'block' }}
                        isHovered={hoveredItemKey === 'dashboard'}
                        isOpen={open}
                        onMouseEnter={() => setHoveredItemKey('dashboard')}
                        onMouseLeave={() => setHoveredItemKey(null)}
                    >
                        <ExpandableButtonWrapper
                            isHovered={hoveredItemKey === 'dashboard'}
                            isOpen={open}
                            isActive={isDashboardActive}
                            bgColor={theme?.colors?.primary}
                        >
                            <ButtonList
                                onClick={() => navigate('/dashboard')}
                                justifyContent={open || hoveredItemKey === 'dashboard' ? 'initial' : 'center'}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: (open || hoveredItemKey === 'dashboard') ? 3 : 'auto',
                                        justifyContent: 'center',
                                        color: isDashboardActive ? theme?.colors?.secondary : theme.colors.text,
                                    }}
                                >
                                    <ChartBar size={28} weight="duotone" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Dashboard"
                                    sx={{
                                        opacity: (open || hoveredItemKey === 'dashboard') ? 1 : 0,
                                        color: isDashboardActive ? theme?.colors?.secondary : theme.colors.text,
                                        '& .MuiTypography-root': { fontWeight: 800 },
                                    }}
                                />
                            </ButtonList>
                        </ExpandableButtonWrapper>
                        <ButtonDivider />
                    </ExpandableListItem>
                    {/* Default Bill – above Add recurring bill; expands on hover, selected covers drop shadow */}
                    <ExpandableListItem
                        key="default-bill"
                        disablePadding
                        sx={{ display: 'block' }}
                        isHovered={hoveredItemKey === 'default-bill'}
                        isOpen={open}
                        onMouseEnter={() => setHoveredItemKey('default-bill')}
                        onMouseLeave={() => setHoveredItemKey(null)}
                    >
                        <ExpandableButtonWrapper
                            isHovered={hoveredItemKey === 'default-bill'}
                            isOpen={open}
                            isActive={isDefaultBillActive}
                            bgColor={theme?.colors?.primary}
                        >
                            <ButtonList
                                onClick={() => {
                                    setSelectedGroup(null);
                                    navigate('/bills/default');
                                }}
                                justifyContent={open || hoveredItemKey === 'default-bill' ? 'initial' : 'center'}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: (open || hoveredItemKey === 'default-bill') ? 3 : 'auto',
                                        justifyContent: 'center',
                                        color: isDefaultBillActive ? theme?.colors?.secondary : theme.colors.text,
                                    }}
                                >
                                    <ShootingStar size={28} weight="duotone" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Default Bill"
                                    sx={{
                                        opacity: (open || hoveredItemKey === 'default-bill') ? 1 : 0,
                                        color: isDefaultBillActive ? theme?.colors?.secondary : theme.colors.text,
                                        '& .MuiTypography-root': { fontWeight: 800 },
                                    }}
                                />
                            </ButtonList>
                        </ExpandableButtonWrapper>
                        <ButtonDivider />
                    </ExpandableListItem>
                    {/*TODO: make it "Add Bill" and have a tooltip saying more*/}
                    <ExpandableListItem
                        key="add-bill"
                        disablePadding
                        sx={{ display: 'block' }}
                        isHovered={hoveredItemKey === 'add-bill'}
                        isOpen={open}
                        onMouseEnter={() => setHoveredItemKey('add-bill')}
                        onMouseLeave={() => setHoveredItemKey(null)}
                    >
                        <ExpandableButtonWrapper
                            isHovered={hoveredItemKey === 'add-bill'}
                            isOpen={open}
                            isActive={isAddBillActive}
                            bgColor={theme?.colors?.primary}
                        >
                            <ButtonList
                                onClick={() => navigate('/bills/templates')}
                                justifyContent={open || hoveredItemKey === 'add-bill' ? 'initial' : 'center'}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: (open || hoveredItemKey === 'add-bill') ? 3 : 'auto',
                                        justifyContent: 'center',
                                        color: isAddBillActive ? theme?.colors?.secondary : theme.colors.text,
                                    }}
                                >
                                    <IconAddScheduled size={28}/>
                                </ListItemIcon>
                                <ListItemText
                                    primary="Add recurring bill"
                                    sx={{
                                        opacity: (open || hoveredItemKey === 'add-bill') ? 1 : 0,
                                        color: isAddBillActive ? theme?.colors?.secondary : theme.colors.text,
                                        '& .MuiTypography-root': {
                                            fontWeight: 800,
                                            transition: 'font-weight 120ms ease, transform 120ms ease, text-shadow 120ms ease',
                                        },
                                    }}
                                />
                            </ButtonList>
                        </ExpandableButtonWrapper>
                        <ButtonDivider />
                    </ExpandableListItem>
                    {activeBills.sort((a, b) => {
                        // Sort by favorite first, then by created_at date (newest first)
                        const favoriteA = getFavoriteStatus(a);
                        const favoriteB = getFavoriteStatus(b);
                        if (favoriteA !== favoriteB) {
                            // If a is favorite, it comes first (-1), otherwise b comes first (1)
                            return favoriteA ? -1 : 1;
                        }
                        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                        return dateB - dateA;
                    }).map((bill) => {
                        const {name, id} = bill;
                        const isFavorite = getFavoriteStatus(bill);
                        const isHovered = hoveredBillId === id;
                        const isActive = String(id) === String(currentTemplateId);
                        return (
                        <ExpandableListItem 
                            key={id} 
                            disablePadding 
                            sx={{ display: 'block' }}
                            isHovered={isHovered}
                            isOpen={open}
                            onMouseEnter={() => setHoveredBillId(id)}
                            onMouseLeave={() => setHoveredBillId(null)}
                        >
                            <ExpandableButtonWrapper 
                                isHovered={isHovered} 
                                isOpen={open} 
                                isActive={isActive}
                                bgColor={theme?.colors?.primary}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <IconButton
                                        onClick={(e) => handleFavoriteToggle(e, id, isFavorite)}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            left: (open || isHovered) ? '0px' : '4px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: isFavorite ? '#ffd700' : theme.colors.text,
                                            opacity: (open || isHovered) ? 0.7 : 0,
                                            '&:hover': {
                                                opacity: 1,
                                            },
                                            padding: '4px',
                                            transition: 'opacity 200ms ease, left 200ms ease',
                                            zIndex: 10,
                                        }}
                                    >
                                        {isFavorite ? (
                                            <StarIcon fontSize="small" />
                                        ) : (
                                            <StarBorderIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                    <ButtonList
                                        onClick={ ()=>navigate(`/bills/templates/${id}`)}
                                        justifyContent={open || isHovered ? 'initial' : 'center'}
                                        px={(open || isHovered) ? 2.5 : 2.5}
                                        sx={{
                                            paddingLeft: (open || isHovered) ? undefined : '20px !important',
                                            paddingRight: (open || isHovered) ? undefined : '20px !important',
                                            ...(open || isHovered) && {
                                                paddingLeft: '32px !important',
                                                paddingRight: '8px !important',
                                            }
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: (open || isHovered) ? 3 : 'auto',
                                                justifyContent: 'center',
                                                color: isActive ? theme.colors.secondary : theme.colors.text,
                                            }}
                                        >
                                            <GetIcon
                                                billTemplateName={name}
                                                style={{
                                                    color: isActive ? theme.colors.secondary : theme.colors.text, 
                                                    fontSize:'28px'
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={name}
                                            sx={{
                                                opacity: (open || isHovered) ? 1 : 0,
                                                color: isActive ? theme.colors.secondary : theme.colors.text,
                                                '& .MuiTypography-root': {
                                                    fontWeight: 800,
                                                    transition: 'font-weight 120ms ease, transform 120ms ease, text-shadow 120ms ease, opacity 200ms ease, color 200ms ease',
                                                },
                                            }}
                                        />
                                    </ButtonList>
                                </Box>
                                {isHovered && (
                                    <Box sx={{ padding: '0 16px', paddingBottom: '8px' }}>
                                        <BillMiniDashboard bill={bill} />
                                    </Box>
                                )}
                            </ExpandableButtonWrapper>
                            <ButtonDivider />
                        </ExpandableListItem>
                        );
                    })}
                    {hasArchived && (
                        <>
                            <ExpandableListItem 
                                key="archived-header" 
                                disablePadding 
                                sx={{ display: 'block' }}
                                isHovered={hoveredItemKey === 'archived-header'}
                                isOpen={open}
                                onMouseEnter={() => setHoveredItemKey('archived-header')}
                                onMouseLeave={() => setHoveredItemKey(null)}
                            >
                                <ExpandableButtonWrapper isHovered={hoveredItemKey === 'archived-header'} isOpen={open} bgColor={theme?.colors?.primary}>
                                    <ButtonList
                                        onClick={() => {
                                            startTransition(() => {
                                                setArchivedExpanded(!archivedExpanded);
                                            });
                                        }}
                                        justifyContent={open || hoveredItemKey === 'archived-header' ? 'initial' : 'center'}
                                    >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: (open || hoveredItemKey === 'archived-header') ? 3 : 'auto',
                                            justifyContent: 'center',
                                            color: theme.colors.text
                                        }}
                                    >
                                        {archivedExpanded ? (
                                            <ExpandLessIcon sx={{ fontSize: '28px' }} />
                                        ) : (
                                            <ExpandMoreIcon sx={{ fontSize: '28px' }} />
                                        )}
                                    </ListItemIcon>
                                    {!archivedExpanded && (
                                        <ListItemText
                                            primary="Archived"
                                            sx={{
                                                opacity: (open || hoveredItemKey === 'archived-header') ? 1 : 0,
                                                color: theme.colors.text,
                                                '& .MuiTypography-root': {
                                                    fontWeight: 800,
                                                    transition: 'font-weight 120ms ease, transform 120ms ease, text-shadow 120ms ease, opacity 200ms ease',
                                                },
                                            }}
                                        />
                                    )}
                                    </ButtonList>
                                </ExpandableButtonWrapper>
                                <ButtonDivider />
                            </ExpandableListItem>
                            {archivedExpanded && (
                                <Suspense fallback={<div style={{ padding: '8px 16px', color: theme?.colors?.text || '#fff' }}>Loading...</div>}>
                                    <List 
                                        component="div" 
                                        disablePadding
                                    >
                                        {archivedBills && archivedBills.length > 0 ? archivedBills.map((bill) => {
                                            try {
                                                if (!bill || !bill.id || !bill.name) return null;
                                                const {name, id} = bill;
                                                const isHovered = hoveredBillId === id;
                                                const isActive = String(id) === String(currentTemplateId);
                                                const spacing = muiTheme?.spacing?.(7) || '56px';
                                                const closedWidth = `calc(${spacing} + 1px)`;
                                                const openWidth = `${drawerWidth}px`;
                                                const currentWidth = isHovered ? openWidth : (open ? openWidth : closedWidth);
                                                
                                                return (
                                                    <ListItem 
                                                        key={id} 
                                                        disablePadding 
                                                        sx={{ display: 'block' }}
                                                        onMouseEnter={() => setHoveredBillId(id)}
                                                        onMouseLeave={() => setHoveredBillId(null)}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                width: currentWidth,
                                                                minWidth: currentWidth,
                                                                maxWidth: currentWidth,
                                                                backgroundColor: theme?.colors?.primary || 'transparent',
                                                                transition: 'width 200ms ease, min-width 200ms ease, max-width 200ms ease, transform 200ms ease',
                                                                boxShadow: isHovered && !open ? '4px 0 8px rgba(0, 0, 0, 0.2)' : 'none',
                                                                transform: isActive ? 'translateX(8px)' : 'none',
                                                                overflow: 'visible',
                                                                zIndex: isActive ? 1302 : (isHovered ? 1301 : 1),
                                                                ...(isActive && {
                                                                    '&::after': {
                                                                        content: '""',
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: '-12px',
                                                                        width: '12px',
                                                                        height: '100%',
                                                                        backgroundColor: theme?.colors?.primary || 'transparent',
                                                                        zIndex: 1303,
                                                                    },
                                                                }),
                                                            }}
                                                        >
                                                            <ButtonList
                                                                onClick={() => navigate(`/bills/templates/${id}`)}
                                                                justifyContent={open || isHovered ? 'initial' : 'center'}
                                                            >
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        mr: (open || isHovered) ? 3 : 'auto',
                                                                        justifyContent: 'center',
                                                                        color: isActive ? theme?.colors?.secondary : (theme?.colors?.text || '#fff'),
                                                                    }}
                                                                >
                                                                    <GetIcon
                                                                        billTemplateName={name}
                                                                        style={{
                                                                            color: isActive ? theme?.colors?.secondary : (theme?.colors?.text || '#fff'), 
                                                                            fontSize:'28px'
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={name}
                                                                    sx={{
                                                                        opacity: (open || isHovered) ? 1 : 0,
                                                                        color: isActive ? theme?.colors?.secondary : (theme?.colors?.text || '#fff'),
                                                                        '& .MuiTypography-root': {
                                                                            fontWeight: 800,
                                                                            transition: 'font-weight 120ms ease, transform 120ms ease, text-shadow 120ms ease, opacity 200ms ease, color 200ms ease',
                                                                        },
                                                                    }}
                                                                />
                                                            </ButtonList>
                                                            {isHovered && (
                                                                <Box sx={{ padding: '0 16px', paddingBottom: '8px' }}>
                                                                    <BillMiniDashboard bill={bill} />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        <ButtonDivider />
                                                    </ListItem>
                                                );
                                            } catch (error) {
                                                return null;
                                            }
                                        }) : (
                                            <ListItem disablePadding sx={{ display: 'block' }}>
                                                <Typography sx={{ color: theme?.colors?.text || '#fff', opacity: open ? 1 : 0, padding: '8px 16px' }}>
                                                    No archived bills
                                                </Typography>
                                            </ListItem>
                                        )}
                                    </List>
                                </Suspense>
                            )}
                        </>
                    )}
                </List>
                </Box>
            </Drawer>
    );
};

export default Sidebar;
